import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  NavLink,
  Spinner,
  CardTable as ClientListTable
} from '../../components'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor
} from '../../utils'

import {
  DEFAULT_RESULTS_PER_PAGE
} from '../../constants'

class DivisionClients extends React.Component {
  static propTypes = {
    divisionId: PropTypes.string.isRequired
  }

  state = {
    pageNav: new PageNavigationControl()
  }

  generateTableDataRows = (dataRows) => {
    let rows = dataRows.map((row, i) => (
      <ClientListTableDataRow data={row} key={i}/>
    ))

    return rows
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    }

    if (this.props.error) {
      return <div>We&rsquo;re experiencing a data loading problem</div>
    }

    let pageNav = this.state.pageNav
    let rows = this.generateTableDataRows(this.props.clients.edges)

    return (
      <div>
        <ClientListTable
          heading='Clients'
          headingIcon='groups'
          tableColumnHeader={<ClientListTableHeaderRow />}
          tableDataRows={rows}
          nextPage={() => pageNav.nextPage(this.props.fetchNext, this.props.divisionId, DEFAULT_RESULTS_PER_PAGE)}
          previousPage={() => pageNav.previousPage(this.props.fetchPrevious, this.props.divisionId, DEFAULT_RESULTS_PER_PAGE)}
          onFirstPage={pageNav.firstPage}
          onLastPage={pageNav.lastPage}
          startCursor={this.props.clients.pageInfo.startCursor}
          totalCount={this.props.clients.totalCount}
          resultsPerPage={DEFAULT_RESULTS_PER_PAGE}
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, DEFAULT_RESULTS_PER_PAGE) })}
        />
      </div>
    )
  }
}

const ClientListTableHeaderRow = () => {
  return (
    <tr className='slds-line-height--reset'>
      <th className='slds-text-title--caps' scope='col'>
        <span className='slds-truncate slds-th__action' title='First name'>First name</span>
      </th>
      <th className='slds-text-title--caps' scope='col'>
        <span className='slds-truncate' title='Last name'>Last name</span>
      </th>
      <th className='slds-text-title--caps' scope='col'>
        <span className='slds-truncate' title='Other name'>Other name</span>
      </th>
      <th className='slds-text-title--caps' scope='col'>
        <span className='slds-truncate' title='Identity number'>Identity number</span>
      </th>
    </tr>
  )
}

const ClientListTableDataRow = (props) => {
  let row = props.data.node

  return (
    <tr className='slds-hint-parent'>
      <th scope='row'>
        <div className='slds-truncate' title={row.identity.firstName}>
          <NavLink to={'/os/identity/' + row.identity.id} >
            {row.identity.firstName}
          </NavLink>
        </div>
      </th>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.identity.lastName}>
          {row.identity.lastName}
        </div>
      </td>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.identity.otherName}>
          {row.identity.otherName}
        </div>
      </td>
      <td role='gridcell'>
        <div className='slds-truncate' title={row.identity.identityValue}>
          {row.identity.identityValue}
        </div>
      </td>
    </tr>
  )
}

const getDivisionClients = gql`query($division: UUID, $resultsPerPage: Int!, $cursor: String) {
    clients(division_Id: $division, first: $resultsPerPage, after: $cursor, orderBy: "identity__last_name") {
      totalCount
      edges {
        node {
          ...ClientInformation
          division {
            id
            name
            employer {
              id
              friendlyName
              identity {
                id
              }
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.clientInformation}
`

const getPreviousDivisionClients = gql`query($division: UUID, $resultsPerPage: Int!, $cursor: String) {
    clients(division_Id: $division, last: $resultsPerPage, before: $cursor, orderBy: "identity__last_name") {
      totalCount
      edges {
        node {
          ...ClientInformation
          division {
            id
            name
            employer {
              id
              friendlyName
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.clientInformation}
`

const DivisionClientsWithData =
  graphql(getDivisionClients, {
    options: ({ divisionId, resultsPerPage = DEFAULT_RESULTS_PER_PAGE, cursor }) => ({
      variables: {
        division: decodeBase64(divisionId),
        resultsPerPage,
        cursor
      }
    }),
    props: ({data: { loading, error, refetch, clients, fetchMore }}) => ({
      loading,
      error,
      refetch,
      clients,
      fetchNext: (divisionId, resultsPerPage) => {
        return fetchMore({
          query: getDivisionClients,
          variables: {
            cursor: clients.pageInfo.endCursor,
            division: decodeBase64(divisionId),
            resultsPerPage
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.clients.edges
            const pageInfo = fetchMoreResult.clients.pageInfo
            const totalCount = fetchMoreResult.clients.totalCount

            return {
              clients: {
                __typename: previousResult.clients.__typename,
                edges: [...newEdges],
                pageInfo,
                totalCount
              }
            }
          }
        })
      },
      fetchPrevious: (divisionId, resultsPerPage) => {
        return fetchMore({
          query: getPreviousDivisionClients,
          variables: {
            cursor: clients.pageInfo.startCursor,
            division: decodeBase64(divisionId),
            resultsPerPage
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.clients.edges
            const pageInfo = fetchMoreResult.clients.pageInfo
            const totalCount = fetchMoreResult.clients.totalCount

            return {
              clients: {
                __typename: previousResult.clients.__typename,
                edges: [...newEdges],
                pageInfo,
                totalCount
              }
            }
          }
        })
      }
    })
  })(DivisionClients)

export default DivisionClientsWithData



// WEBPACK FOOTER //
// ./src/components/division-clients/index.js